import type { IPartFilter, IPartSortByOption } from "@contentful-api/types/contentful";
import type { Facets } from "@store/index";
import { useStore } from "@store/index";
import { useFilterStore } from "@store/filterStore";
import type { FC, ReactNode } from "react";
import { useEffect, useCallback, useMemo, use } from "react";
import {
    type ConfigureProps,
    useClearRefinements,
    useConfigure,
    useCurrentRefinements,
    useInfiniteHits,
    useRefinementList,
} from "react-instantsearch";
import { FiltersContext } from "./FiltersContext";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import type { AlgoliaProduct, AlgoliaProductProperties } from "@lib/wrappers/algolia/types";
import { INITIAL_HITS_PER_PAGE } from "@components/commerce/product/list/product-list-footer/ProductListFooter";
import { createAlgoliaFiltersString } from "@lib/algolia/algoliaFilters";
import { activeSiteConfig } from "@config/site/site-config";
import { useClientLocation } from "@hooks/useClientLocation";
import { isStoresFacet } from "@lib/utils/plp/mapStoreFacetOptions";
import { getStoreSizeCrossFilters } from "@lib/utils/plp/storeSizesFilter";
import { STORE_SIZES_AGG_FACET } from "@lib/constants/filtersConstants";

interface IFiltersControllerProps {
    children: ReactNode;
    listOfFilters: IPartFilter[];
    sortByOptionsList: IPartSortByOption[];
    page: number;
    localizedPageCategory?: string;
    enPageCategory?: string;
}

/**
 * Mounts InstantSearch hooks needed for store↔size cross-filtering.
 * Only rendered when the PLP has a stores facet so non-store pages skip STORE_SIZES_AGG_FACET.
 */
const StoreSizeCrossFiltersProvider: FC<{ children: ReactNode; sizeLocale: string }> = ({
    children,
    sizeLocale,
}) => {
    const baseContext = use(FiltersContext);
    const { items: currentRefinements } = useCurrentRefinements();
    const { items: hits } = useInfiniteHits<AlgoliaProduct>();
    const { items: storeSizesAggItems } = useRefinementList({
        attribute: STORE_SIZES_AGG_FACET,
        limit: 1000,
    });

    const storeSizesAggValues = useMemo(
        () => storeSizesAggItems.filter((item) => item.count > 0).map((item) => String(item.value)),
        [storeSizesAggItems]
    );

    const contextValue = useMemo(() => {
        const { storeIdsForSelectedSizes, sizeValuesForSelectedStores } = getStoreSizeCrossFilters(
            currentRefinements,
            storeSizesAggValues,
            sizeLocale,
            hits
        );

        return {
            ...baseContext,
            storeIdsForSelectedSizes,
            sizeValuesForSelectedStores,
        };
    }, [baseContext, currentRefinements, storeSizesAggValues, sizeLocale, hits]);

    return <FiltersContext value={contextValue}>{children}</FiltersContext>;
};

export const FiltersController: FC<IFiltersControllerProps> = ({
    children,
    listOfFilters,
    sortByOptionsList,
    localizedPageCategory,
    page = 1,
    enPageCategory,
}) => {
    const category = activeSiteConfig.hasLocalizedCategoryFacets
        ? localizedPageCategory
        : enPageCategory;

    const hasStoresFacet = listOfFilters?.some((filter) =>
        isStoresFacet(filter?.fields?.facetName)
    );
    const extraAttributesToRetrieve: AlgoliaProductProperties[] = hasStoresFacet
        ? ["fullSizes"]
        : [];

    useConfigure({
        filters: createAlgoliaFiltersString({
            category,
        }),
        hitsPerPage: INITIAL_HITS_PER_PAGE * page,
        facetingAfterDistinct: true,
        attributesToRetrieve: [
            ...DEFAULT_ATTRIBUTES_TO_RETRIEVE,
            "descriptiveHeading",
            ...extraAttributesToRetrieve,
        ] as AlgoliaProductProperties[],
        page: 0,
    } as ConfigureProps);

    // Global store access (unchanged)
    const globalShoeSizeConvention = useStore((state) => state.data.me?.globalShoeSizeConvention);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);

    // Static refinement function
    const { refine: clearRefinements } = useClearRefinements();

    // Initialize filter map in store if needed
    const initializeFilters = useCallback(() => {
        const filtersMap: Facets = {};
        listOfFilters?.forEach((filter) => {
            filtersMap[filter?.fields?.filterName] = { refine: () => {}, items: [] };
        });
        useFilterStore.getState().setFilterItems(filtersMap);
    }, [listOfFilters]);

    // Initialize filters on mount
    useEffect(() => {
        initializeFilters();
    }, [initializeFilters]);

    const clientLocation = useClientLocation(hasStoresFacet);
    const sizeLocale = globalShoeSizeConvention ?? "";

    const contextValue = useMemo(
        () => ({
            listOfFilters,
            sortByOptionsList,
            clearRefinements,
            sizeLocale,
            setSizeLocale: setGlobalShoeSizeConvention,
            clientLocation,
            storeIdsForSelectedSizes: null,
            sizeValuesForSelectedStores: null,
        }),
        [
            listOfFilters,
            sortByOptionsList,
            clearRefinements,
            sizeLocale,
            setGlobalShoeSizeConvention,
            clientLocation,
        ]
    );

    return (
        <FiltersContext.Provider value={contextValue}>
            {hasStoresFacet ? (
                <StoreSizeCrossFiltersProvider sizeLocale={sizeLocale}>
                    {children}
                </StoreSizeCrossFiltersProvider>
            ) : (
                children
            )}
        </FiltersContext.Provider>
    );
};
